import { TpETLPipelineComponent } from '../interfaces';
import { TpETLComponentTypeManifest, TpETLOutputObject } from '../etl-component-types/interfaces';
import { FORM_DATATYPES, promptAutoForm, TpAutoFormDef } from '../../../components/auto-form-modal/AutoForm';

type TpETLObjectPath = {
  name: string;
  url: string;
};

type TpMultipleOutputs = {
  [outputId: string]: string | TpETLObjectPath[];
};

type TpOutputIdToName = {
  [outputId: string]: string;
};

/**
 * Utility function to get the path options for a given ETL object path.
 * Due to backwards compatibility, the output path can either be:
 * - a string (single file for single output)
 * - an array of TpETLObjectPath (multiple files for a single output)
 * - an object with keys for each output and the value being either:
 *   - a string (single file for the output)
 *   - an array of TpETLObjectPath (multiple files for the output)
 *
 * Convert all of these cases to an array of TpETLObjectPath.
 */
function pathOptions(
  outputs: string | TpETLObjectPath[] | TpMultipleOutputs,
  outputIdToName: TpOutputIdToName,
): TpETLObjectPath[] | null {
  if (typeof outputs === 'string') {
    // Single output path as a string
    return [{ name: 'Output', url: outputs }];
  }
  if (Array.isArray(outputs)) {
    // Multiple output paths as an array of TpETLObjectPath
    return outputs;
  }
  if (typeof outputs === 'object' && outputs !== null) {
    // Multiple outputs as an object with output IDs as keys
    return Object.entries(outputs).flatMap(([outputId, outputPath]) => {
      if (typeof outputPath === 'string') {
        // Single output path for this output ID
        return [{ name: outputIdToName[outputId] ?? outputId, url: outputPath }];
      } else if (Array.isArray(outputPath)) {
        // Multiple output paths for this output ID
        return outputPath.map((path) => ({
          name: `${outputIdToName[outputId] ?? outputId} - ${path.name}`, // Combine result and file name
          url: path.url,
        }));
      } else {
        console.error(`Unexpected output path type for output ID ${outputId}: ${typeof outputPath}`);
        return []; // Skip invalid output paths
      }
    });
  }

  console.error(`Unexpected output path type: ${typeof outputs}`);
  return null; // No valid output paths available
}

export class DownloadHelper {
  constructor(
    private lastComponentProgress?: { [id: string]: any },
    private runningComponentProgress?: { [id: string]: any },
  ) {}

  getProgressResult(compId: string) {
    const hasLastResult =
      !!this.lastComponentProgress &&
      Object.prototype.hasOwnProperty.call(this.lastComponentProgress, compId) &&
      this.lastComponentProgress[compId].output_path;
    if (hasLastResult) return this.lastComponentProgress![compId];
    const hasRunningResult =
      !!this.runningComponentProgress &&
      Object.prototype.hasOwnProperty.call(this.runningComponentProgress, compId) &&
      this.runningComponentProgress[compId].output_path;
    if (hasRunningResult) return this.runningComponentProgress![compId];

    return null; // No results available for this component
  }

  getProgressLogs(compId: string) {
    const hasLastResult =
      !!this.lastComponentProgress &&
      Object.prototype.hasOwnProperty.call(this.lastComponentProgress, compId) &&
      this.lastComponentProgress[compId].log_path;
    if (hasLastResult) return this.lastComponentProgress![compId];
    const hasRunningResult =
      !!this.runningComponentProgress &&
      Object.prototype.hasOwnProperty.call(this.runningComponentProgress, compId) &&
      this.runningComponentProgress[compId].log_path;
    if (hasRunningResult) return this.runningComponentProgress![compId];

    return null; // No logs available for this component
  }

  getProgressResultPath(
    comp: TpETLPipelineComponent,
    compManifest: TpETLComponentTypeManifest,
  ): Promise<string | null> {
    // Get the output names from the component manifest
    const outputNames = compManifest.outputs.reduce((acc: TpOutputIdToName, output: TpETLOutputObject) => {
      acc[output.id] = output.name;
      return acc;
    }, {} as TpOutputIdToName);

    const outputPaths = pathOptions(this.getProgressResult(comp.id)?.output_path, outputNames);

    // No output path available
    if (!outputPaths || outputPaths.length === 0) {
      return Promise.resolve(null);
    }

    // Single output path, return it directly
    if (outputPaths.length == 1) {
      return Promise.resolve(outputPaths[0].url);
    }

    const form: TpAutoFormDef = {
      title: 'Select result',
      fields: [
        {
          id: 'resultId',
          name: 'Result',
          dataType: FORM_DATATYPES.MULTIPLECHOICE,
          startContent: '0', // Default to the first option
          choices: outputPaths.map((outputPath, index) => {
            return {
              id: index.toString(), // Use index as ID for simplicity
              content: outputPath.name,
            };
          }),
          required: true,
        },
      ],
    };

    return promptAutoForm(form).then(
      (dataForm: any) => {
        const selection: string = dataForm.resultId;
        const index = +selection; // Convert selection to number
        if (isNaN(index) || index < 0 || index >= outputPaths.length) {
          throw new Error(`Invalid selection: ${selection}`);
        }
        return outputPaths[index].url;
      },
      () => null, // Handle cancellation
    );
  }
}
